import { faCalendarAlt, faClock } from '@fortawesome/free-regular-svg-icons';
import {
  faCheck,
  faCopy,
  faKey,
  faPen,
  faPlus,
  faSyncAlt,
  faTag,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import ApiKeyEditModal from 'components/modals/ApiKeyEditModal';
import ApiKeyRegistrationModal from 'components/modals/ApiKeyRegistrationModal';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import ApiKeyService from 'services/ApiKey.service';

const ApiKeysRegistration = () => {
  const formatDateTime = useFormatDateTime();
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    includeInactives: 'true'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [apiKeys, setApiKeys] = useState({
    content: [],
    totalElements: 0
  });
  const [showModal, setShowModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState({});
  const [showModalDelete, setShowModalDelete] = useState(false);
  const { showSuccessNotification, showErrorNotification } = useNotification();
  useEffect(() => {
    getData();
  }, [filters]);

  const getData = async () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams(filters).toString();
    const response = await ApiKeyService.list(queryParams);
    if (response.content) setApiKeys(response);
    setIsEmpty(response.content.length === 0);
    setIsLoading(false);
  };

  const columns = [
    {
      accessorKey: 'description',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { description } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faTag} className="me-2" />
            <span>{description}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'key',
      header: 'Chave',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { keyValue } = rowData.row.original;

        // Show first 3 characters + asterisks for the rest
        const maskedKey =
          keyValue.length > 3
            ? keyValue.substring(0, 3) + '*'.repeat(keyValue.length - 3)
            : keyValue;

        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faKey} className="me-2" />
            <span>{maskedKey}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Data de criação',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
            <span>{formatDateTime(creationDate)}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'lastUpdateDate',
      header: 'Data de atualização',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { lastUpdateDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
            <span>{formatDateTime(lastUpdateDate)}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'lastUsedDate',
      header: 'Data de último uso',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { lastUsedDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faClock} className="me-2" />
            <span>
              {lastUsedDate
                ? formatDateTime(lastUsedDate)
                : 'Ainda não foi usado'}
            </span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return (
          <FontAwesomeIcon
            icon={inactive ? faTimes : faCheck}
            className={inactive ? 'text-danger' : 'text-success'}
          />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <Flex alignItems="center">
            <OverlayTrigger overlay={<Tooltip>Ativar/Desativar</Tooltip>}>
              <Form.Switch
                variant="link"
                className="text-primary"
                checked={!item.inactive}
                onChange={() => handleToggleStatus(item.id)}
              />
            </OverlayTrigger>
            <Button
              variant="link"
              className="text-primary px-2"
              onClick={() => handleEdit(item)}
            >
              <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                <span>
                  <FontAwesomeIcon icon={faPen} size="sm" />
                </span>
              </OverlayTrigger>
            </Button>

            <Button
              variant="link"
              className="text-primary px-2"
              onClick={() => handleCopy(item.keyValue)}
            >
              <OverlayTrigger overlay={<Tooltip>Copiar</Tooltip>}>
                <span>
                  <FontAwesomeIcon icon={faCopy} size="sm" />
                </span>
              </OverlayTrigger>
            </Button>

            <Button
              variant="link"
              className="text-danger px-2"
              onClick={() => handleDelete(item)}
            >
              <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                <span>
                  <FontAwesomeIcon icon={faTrash} size="sm" />
                </span>
              </OverlayTrigger>
            </Button>
          </Flex>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    columns,
    data: apiKeys.content,
    pagination: true,
    perPage: 10
  });

  const handleEdit = item => {
    setSelectedApiKey(item);
    setShowModalEdit(true);
  };

  const handleCopy = keyValue => {
    navigator.clipboard.writeText(keyValue);
    showSuccessNotification('Chave de API copiada com sucesso');
  };

  const handleDelete = item => {
    setSelectedApiKey(item);
    setShowModalDelete(true);
  };

  const handleToggleStatus = async id => {
    const response = await ApiKeyService.toggleStatus(id);
    if (response.id) {
      showSuccessNotification(
        `Chave de API ${
          !response.inactive ? 'ativada' : 'desativada'
        } com sucesso`
      );
      getData();
    } else {
      showErrorNotification(response.userMessage);
    }
  };

  const deleteApiKey = async response => {
    if (response) {
      const response = await ApiKeyService.delete(selectedApiKey.id);
      if (response.ok) {
        showSuccessNotification('Chave de API excluída com sucesso');
        getData();
      } else {
        showErrorNotification(response.userMessage);
      }
    }
    setShowModalDelete(false);
  };

  return (
    <>
      <ApiKeyRegistrationModal
        show={showModal}
        onClose={() => setShowModal(false)}
        refresh={getData}
      />
      <ApiKeyEditModal
        show={showModalEdit}
        onClose={() => setShowModalEdit(false)}
        refresh={getData}
        selectedApiKey={selectedApiKey}
      />
      <DialogModal
        showModal={showModalDelete}
        handleClose={() => setShowModalDelete(false)}
        message="Tem certeza que deseja excluir esta chave de API?"
        response={deleteApiKey}
      />
      <Row className="g-2">
        <Col xl={9}>
          <FalconComponentCard>
            <FalconComponentCard.Header title="Chaves de API">
              <Flex justifyContent="between" className="w-100">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
                  Nova Chave
                </Button>
              </Flex>
            </FalconComponentCard.Header>
            <FalconComponentCard.Body>
              <Flex justifyContent="end" className="mb-3">
                <Form.Select
                  size="sm"
                  style={{ width: '200px' }}
                  className="rounded-pill"
                  onChange={e =>
                    setFilters(prevFilters => ({
                      ...prevFilters,
                      includeInactives: e.target.value
                    }))
                  }
                >
                  <option value="true">Filtro por status</option>
                  <option value="true">Todos</option>
                  <option value="false">Somente ativos</option>
                </Form.Select>
              </Flex>

              {isLoading ? (
                <Skeleton count={10} height={44} baseColor="#5E6E82" />
              ) : isEmpty ? (
                <div style={{ minHeight: '300px' }}>
                  <EmptyList imgSize={500} vhSize={73} />
                </div>
              ) : (
                <>
                  <AdvanceTableProvider {...table}>
                    <AdvanceTable
                      table
                      headerClassName="text-nowrap align-middle"
                      rowClassName="align-middle white-space-nowrap"
                      tableProps={{
                        striped: true,
                        className: 'fs-10 mb-0 overflow-hidden'
                      }}
                    />
                  </AdvanceTableProvider>
                  <Pagination
                    data={apiKeys}
                    filters={filters}
                    setFilters={setFilters}
                  />
                </>
              )}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>

        <Col xl={3}>
          <FalconComponentCard>
            <FalconComponentCard.Body>
              <h3 className="m-0 mb-3">Para que serve as chaves de API?</h3>
              <p>
                As chaves de API são um token permanente que permite que você
                autentique e autorize aplicativos de terceiros acessarem a API
                do MSP Desk para os escopos selecionados sem a necessidade de
                fornecer credenciais de acesso diretamente ao aplicativo. Basta
                criar uma chave e usá-lo para autenticação onde quiser.
              </p>
              <p>
                Use a chave como parâmetro <code>Bearer</code> do cabeçalho{' '}
                <code>Authorization</code> da requisição.
              </p>

              <p className="fw-bold mb-0">Endereço base</p>
              <div className="bg-200 rounded p-2 mb-3">
                https://api.mspdesk.com.br/v1
              </div>

              <p className="fw-bold mb-0">Header (Cabecalho da requisição)</p>
              <div className="bg-200 rounded p-2">
                Authorization: Bearer {`\{chave}`}
              </div>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
    </>
  );
};

export default ApiKeysRegistration;
